<script setup lang="ts">
/**
 * PasswordInput — 密码输入框(带查看/隐藏密码开关,默认隐藏)
 * - v-model 双向绑定
 * - 右侧眼睛图标点击切换明文/密文,默认密文
 * - error 时输入框红框(配合外层实时校验提示)
 */
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import YfbIcon from '@/components/YfbIcon.vue'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    modelValue: string
    id?: string
    placeholder?: string
    autocomplete?: string
    error?: boolean
  }>(),
  { id: undefined, placeholder: undefined, autocomplete: undefined, error: false }
)

const emit = defineEmits<{ (e: 'update:modelValue', v: string): void }>()

// 密码可见性开关:默认隐藏
const visible = ref(false)

function onInput(e: Event) {
  emit('update:modelValue', (e.target as HTMLInputElement).value)
}
</script>

<template>
  <div class="pwd-input" :class="{ 'pwd-input--error': props.error }">
    <input
      :id="props.id"
      class="pwd-input__field"
      :type="visible ? 'text' : 'password'"
      :placeholder="props.placeholder"
      :autocomplete="props.autocomplete"
      :value="props.modelValue"
      @input="onInput"
    />
    <button
      type="button"
      class="pwd-input__toggle"
      :aria-label="visible ? t('login.hidePassword') : t('login.showPassword')"
      :title="visible ? t('login.hidePassword') : t('login.showPassword')"
      @click="visible = !visible"
    >
      <YfbIcon :name="visible ? 'ph:eye-bold' : 'ph:eye-slash-bold'" :size="18" />
    </button>
  </div>
</template>

<style scoped>
.pwd-input {
  position: relative;
  width: 100%;
}

.pwd-input__field {
  width: 100%;
  padding: var(--yfb-sp-3) var(--yfb-sp-10) var(--yfb-sp-3) var(--yfb-sp-4);
  font-size: var(--yfb-fs-base);
  color: var(--yfb-ink);
  background: var(--yfb-bg);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  transition: border-color var(--yfb-anim-fast), box-shadow var(--yfb-anim-fast);
}

.pwd-input__field:focus {
  outline: none;
  border-color: var(--yfb-accent);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--yfb-accent) 25%, transparent);
}

.pwd-input--error .pwd-input__field {
  border-color: var(--yfb-signal-danger);
}

.pwd-input__toggle {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  display: grid;
  place-items: center;
  padding: var(--yfb-sp-2);
  color: var(--yfb-ink-soft);
  background: transparent;
  border: none;
  border-radius: var(--yfb-radius-sm);
  cursor: pointer;
  transition: color var(--yfb-anim-fast);
}

.pwd-input__toggle:hover {
  color: var(--yfb-accent);
}

/* 移动端：字号提到 16px，避免 iOS Safari 对 <16px 输入框聚焦强制缩放 */
@media (max-width: 640px) {
  .pwd-input__field {
    font-size: 1rem;
    padding-top: var(--yfb-sp-2);
    padding-bottom: var(--yfb-sp-2);
  }
}
</style>
